import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Package, Hammer, Puzzle, Truck, Wallet, ArrowUpDown, Home as HomeIcon, Layers, Check, Plus, Star } from "lucide-react";
import { useProduct, useProducts, http, inr } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { FlowImage } from "../components/FlowImage";
import { Stars } from "../components/Stars";
import { PincodeChecker } from "../components/PincodeChecker";
import { Reveal, Stagger, Item, Marquee } from "../components/Motion";
import { renderOf } from "../components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Seo, faqLd } from "../components/Seo";

const G = (id) => `https://static.prod-images.emergentagent.com/jobs/b9979ec9-3304-4896-8d2c-2eeafd09334a/images/${id}.jpeg`;
const LAUNCH = { oak: G("f36ad9a81edc6d19a3ed277380af2ebba27be6c1dcf6c09c7849254fc65296fc"), walnut: G("a3ccfd4a11db1a5ca1d6a7ec5e3bbf94dcd83fdb7910f732d14b070727905a17") };
const HANDS = G("71f0994ac08e770cabe5c3ef602adfb29d3e428802c560a1339ec45bd1c84815");
const MODULAR = G("38a9fbb45ad19e56b9469279d73c22a17dbffe79c7e088de62ff152aff06c6ab");

const FinishToggle = ({ variants, value, onChange, testid, dark }) => (
  <div className={`inline-flex rounded-full p-1 ${dark ? "bg-linen/10" : "bg-card shadow-card"}`} role="radiogroup" aria-label="Finish">
    {variants.map((v) => (
      <button key={v.key} role="radio" aria-checked={v.key === value} data-testid={`${testid}-${v.key}`} onClick={() => onChange(v.key)} className={`relative flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${v.key === value ? (dark ? "text-walnut" : "text-linen") : dark ? "text-linen/80" : "text-walnut/70"}`}>
        {v.key === value && <motion.span layoutId={`${testid}-pill`} className={`absolute inset-0 rounded-full ${dark ? "bg-linen" : "bg-walnut"}`} transition={{ type: "spring", stiffness: 400, damping: 30 }} />}
        <span style={{ backgroundColor: v.swatch }} className="relative h-4 w-4 rounded-full ring-2 ring-white/60" /><span className="relative">{v.name}</span>
      </button>
    ))}
  </div>
);

const Hero = () => {
  const { data } = useProduct("pustak-rack");
  const [k, setK] = useState("oak");
  const p = data?.product;
  const variants = p?.variants || [{ key: "oak", name: "Oak", swatch: "#D8A25A" }, { key: "walnut", name: "Walnut", swatch: "#5B3A27" }];
  const render = p ? renderOf(p, k) : `/studio/rack-${k}.jpg`;
  return (
    <section data-testid="home-hero" className="relative overflow-hidden">
      <div className="container-hw grid items-center gap-10 py-10 lg:grid-cols-12 lg:gap-8 lg:py-16">
        <div className="lg:col-span-6">
          <p className="eyebrow rise">Build it together · Hyderabad</p>
          <h1 className="t-display rise d1 mt-5">DIY modular furniture for homes that grow.</h1>
          <p className="t-lead rise d2 mt-6 max-w-lg">
            The Pustak Rack flat-packs to your door, builds in one evening with the hex key in the box, and takes drawer, lift-top and door modules whenever life fills up.
          </p>
          <div className="rise d3 mt-8 flex flex-wrap gap-3">
            <Link to={`/product/pustak-rack?finish=${k}`} data-testid="hero-shop-button" className="btn-sheesham w-full px-7 py-3.5 text-base sm:w-auto">Shop the Pustak Rack <ArrowRight className="h-4 w-4" /></Link>
            <a href="#how-it-builds" data-testid="hero-how-button" className="btn-outline w-full border-neem px-7 py-3.5 text-base text-neem hover:border-neem hover:bg-neem hover:text-linen sm:w-auto">See how it builds</a>
          </div>
          <dl className="rise d4 mt-10 grid max-w-lg grid-cols-3 gap-4 text-sm sm:gap-6">
            {[[Package, "Flat-packed", "builds at home"], [Puzzle, "Modular", "add modules anytime"], [Truck, "24 Hrs", "free delivery in HYD"]].map(([I, a, b]) => (
              <div key={a} className="rounded-2xl border border-jute/50 bg-card/60 p-3 sm:p-4"><I className="mb-2 h-5 w-5 text-sheesham" /><dt className="font-sans text-base font-semibold leading-tight">{a}</dt><dd className="t-small">{b}</dd></div>
            ))}
          </dl>
        </div>
        <div className="relative lg:col-span-6">
          <div className="render rise d2 aspect-square rounded-[2rem] shadow-card lg:aspect-[5/5.2]">
            <AnimatePresence mode="sync" initial={false}>
              <motion.img key={render} src={render} alt={`Pustak Rack in ${k}`} initial={{ opacity: 0, scale: 1.03 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5, ease: [0.2, 0.7, 0.2, 1] }} className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
            </AnimatePresence>
            <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-linen/95 px-3 py-1.5 text-xs font-semibold backdrop-blur sm:left-5 sm:top-5"><Star className="h-3.5 w-3.5 fill-brass text-brass" /> New launch</div>
            <div className="absolute bottom-4 left-4 sm:bottom-5 sm:left-5"><FinishToggle variants={variants} value={k} onChange={setK} testid="hero-finish" /></div>
          </div>
          <Link to={`/product/pustak-rack?finish=${k}`} data-testid="hero-featured-card" className="card card-hover rise d4 mt-4 flex items-center justify-between gap-4 p-4 pr-5 sm:absolute sm:-bottom-5 sm:right-6 sm:mt-0 sm:justify-start">
            <div>
              <p className="t-h4 leading-tight">Pustak Rack</p>
              <p className="t-small">7 shelves · 212.4 cm · {variants.find((v) => v.key === k)?.name}</p>
            </div>
            <p className="price text-2xl">{inr(p?.price || 4999)}<span className="ml-1 text-xs font-sans font-normal text-muted-foreground">+GST</span></p>
          </Link>
        </div>
      </div>
    </section>
  );
};

const Ticker = () => (
  <div className="bg-walnut py-4 text-linen">
    <Marquee items={["24 Hrs free delivery across Hyderabad", "No carpenter needed", "One hex key", "Oak or Walnut", "Pay on delivery", "10-day returns", "3-year warranty", "Code HW10 for 10% off"]} />
  </div>
);

const Lineup = () => {
  const { data = [] } = useProducts();
  const { addToCart } = useStore();
  const rack = data.find((p) => p.slug === "pustak-rack");
  const mods = ["pustak-drawer", "pustak-lift-top", "pustak-door"].map((s) => data.find((p) => p.slug === s)).filter(Boolean);
  if (!rack) return null;
  return (
    <section data-testid="home-lineup" className="container-hw py-16 lg:py-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="eyebrow">The system</p>
          <h2 className="t-h2 mt-3">One rack. Three modules. Endless rooms.</h2>
        </div>
        <Link to="/shop" data-testid="lineup-shop-link" className="btn-outline">Shop everything <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
      <Stagger className="mt-10 grid gap-4 md:grid-cols-12">
        <Item className="md:col-span-5">
          <Link to="/product/pustak-rack" data-testid="lineup-rack" className="render card-hover group block aspect-square">
            <img src={renderOf(rack)} alt="Pustak Rack in Oak" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            <div className="absolute inset-x-4 bottom-4 flex items-end justify-between gap-4 rounded-xl bg-linen/95 p-4 backdrop-blur">
              <div><p className="eyebrow">Start here</p><p className="t-h3 mt-1">{rack.name}</p><p className="t-small mt-1 line-clamp-1">{rack.tagline}</p></div>
              <p className="price shrink-0 text-2xl">{inr(rack.price)}</p>
            </div>
          </Link>
        </Item>
        <div className="grid gap-4 md:col-span-7">
          {mods.map((m) => (
            <Item key={m.slug} className="h-full">
              <div data-testid={`lineup-${m.slug}`} className="card card-hover group flex h-full items-center gap-4 p-3 sm:p-4">
                <Link to={`/product/${m.slug}`} className="render h-24 w-24 shrink-0 rounded-xl sm:h-full sm:w-36"><img src={renderOf(m)} alt={m.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/product/${m.slug}`} className="t-h4 block leading-tight hover:text-sheesham">{m.name}</Link>
                  <p className="t-small mt-1 line-clamp-2">{m.features[0]}</p>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="price text-lg">{inr(m.price)}</p>
                    <button data-testid={`lineup-add-${m.slug}`} onClick={() => addToCart(m)} className="btn-sheesham px-3.5 py-1.5 text-xs"><Plus className="h-3.5 w-3.5" /> Add</button>
                  </div>
                </div>
              </div>
            </Item>
          ))}
        </div>
      </Stagger>
    </section>
  );
};

const InHomes = () => {
  const { data } = useProduct("pustak-rack");
  const [k, setK] = useState("oak");
  if (!data) return null;
  const p = data.product;
  const v = p.variants.find((x) => x.key === k) || p.variants[0];
  return (
    <section data-testid="home-new-launch" className="grid overflow-hidden rounded-[2rem] bg-walnut text-linen lg:grid-cols-2">
      <div className="relative min-h-[420px] lg:min-h-[640px]">
        <AnimatePresence mode="sync" initial={false}>
          <motion.img key={k} src={LAUNCH[k]} alt={`${p.name} in ${v.name} finish in a Hyderabad home`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }} className="absolute inset-0 h-full w-full object-cover object-top" data-testid="launch-image" />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-walnut via-transparent to-transparent lg:bg-gradient-to-r" />
      </div>
      <Reveal className="flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-14 lg:py-20">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brass">Seen in Hyderabad homes</p>
        <h2 className="t-h2 mt-5 text-linen">{p.name}, in {v.name}.</h2>
        <p className="t-lead mt-5 max-w-md text-linen/70">{p.description}</p>
        <div className="mt-8"><FinishToggle variants={p.variants} value={k} onChange={setK} testid="launch-variant" dark /></div>
        <div className="mt-8 flex flex-wrap items-center gap-6">
          <div><p data-testid="launch-price" className="price text-4xl text-linen">{inr(p.price)}</p><p className="t-small text-linen/60">Excluding tax</p></div>
          <Link to={`/product/${p.slug}?finish=${v.key}`} data-testid="launch-shop-now-button" className="btn-sheesham px-7 py-3.5">Shop now <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <ul className="mt-10 grid gap-3 border-t border-linen/10 pt-6 text-sm text-linen/75 sm:grid-cols-2">
          {p.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-brass" />{f}</li>)}
        </ul>
      </Reveal>
    </section>
  );
};

const WHY = [
  [Wallet, "An honest price", "You do the last bit of work at home, so you don't pay for a showroom, a middleman or a carpenter's visit."],
  [ArrowUpDown, "Fits most lifts", "Everything ships flat. No hauling a built cabinet up the stairs or squeezing it round a corner."],
  [HomeIcon, "Moves with you", "Bolts, not glue. Take it apart for the next flat and build it again in the new one."],
  [Layers, "Grows with you", "Start with one rack. Add a drawer, a lift-top or doors. Everything fits the same bays."],
];

const WhyDIY = () => (
  <section data-testid="home-why-diy" className="container-hw py-16 lg:py-24">
    <Reveal className="max-w-2xl">
      <p className="eyebrow">Why do it yourself</p>
      <h2 className="t-h2 mt-3">Furniture that's yours, because you built it.</h2>
      <p className="t-lead mt-5">Do-it-yourself modular furniture isn't a compromise. It's how a well-made piece gets cheaper to buy, easier to move and simple to add to.</p>
    </Reveal>
    <Stagger className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {WHY.map(([I, t, d]) => (
        <Item key={t} className="card card-hover p-6 sm:p-7">
          <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sand text-sheesham"><I className="h-5 w-5" /></span>
          <p className="t-h4 mt-6">{t}</p>
          <p className="t-small mt-3 leading-relaxed">{d}</p>
        </Item>
      ))}
    </Stagger>
  </section>
);

const STEPS = [
  [Package, "Unbox", "It flat-packs to your door. Every part is numbered, and the illustrated guide shows each step."],
  [Hammer, "Build", "The hex key is in the box. Two pairs of hands, one evening, no carpenter."],
  [Puzzle, "Grow", "Slide a drawer, lift-top or doors into any bay. Add-ons fit the same system."],
];

const HowItBuilds = () => (
  <section id="how-it-builds" data-testid="how-it-builds" className="container-hw scroll-mt-28 py-16 lg:py-24">
    <div className="grain relative overflow-hidden rounded-[2rem] bg-walnut text-linen">
      <div className="relative grid gap-10 p-6 sm:p-10 lg:grid-cols-12 lg:p-16">
        <Reveal className="lg:col-span-5">
          <FlowImage src={HANDS} alt="Two pairs of hands building a Pustak Rack with a hex key" fade="soft" testid="how-image" className="aspect-[4/5] rounded-2xl" />
        </Reveal>
        <div className="flex flex-col justify-center lg:col-span-6 lg:col-start-7">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brass">How it builds</p>
            <h2 className="t-h2 mt-4 text-linen">One evening. No carpenter.</h2>
            <p className="t-lead mt-5 max-w-lg text-linen/70">Do it yourself, not by yourself. We design every part so two people who have never held a hex key can build it in one evening, and we answer on care@hwfurniture.in if you get stuck.</p>
          </Reveal>
          <Stagger className="mt-10 space-y-4">
            {STEPS.map(([I, t, d], i) => (
              <Item key={t} className="flex gap-5 rounded-2xl border border-linen/10 bg-linen/5 p-5">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-neem text-linen"><I className="h-5 w-5" /></span>
                <div><p className="t-h4 text-linen">{i + 1}. {t}</p><p className="t-small mt-1 text-linen/70">{d}</p></div>
              </Item>
            ))}
          </Stagger>
        </div>
      </div>
    </div>
  </section>
);

const Modular = () => (
  <section data-testid="home-modular" className="container-hw grid gap-12 py-8 lg:grid-cols-12 lg:py-16">
    <Reveal className="lg:col-span-5">
      <p className="eyebrow">One modular system</p>
      <h2 className="t-h2 mt-3">Start with one rack. Add as life fills up.</h2>
      <ol className="mt-10 space-y-6">
        {[["Start", "Build one Pustak Rack: seven open shelves for books, plants and everything in between."], ["Add", "Need more room? Add a shelf anytime. It fits the frame you already have."], ["Extend", "Slide a Drawer, Lift-top or Door module into any bay. Open shelving becomes closed storage or a standing desk, with the same hex key."]].map(([t, d], i) => (
          <li key={t} className="flex gap-5">
            <span className="price text-3xl text-neem">0{i + 1}</span>
            <div><p className="t-h4">{t}</p><p className="t-small mt-1 leading-relaxed">{d}</p></div>
          </li>
        ))}
      </ol>
      <Link to="/product/pustak-rack" data-testid="modular-shop-button" className="btn-sheesham mt-10">Start with the Pustak Rack</Link>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
      <FlowImage src={MODULAR} alt="Two Pustak Racks joined side by side as one modular wall unit" fade="soft" testid="modular-image" className="aspect-[4/5] rounded-[2rem] lg:aspect-[3/4]" />
    </Reveal>
  </section>
);

const MODULE_VIEWS = { "pustak-door": "Closed view", "pustak-lift-top": "Lift-top (work / dine)", "pustak-drawer": "Drawer storage" };
const MODULE_ORDER = ["pustak-door", "pustak-lift-top", "pustak-drawer"];

const BuildYourWay = () => {
  const { data = [] } = useProducts({ category: "modules" });
  const { addToCart } = useStore();
  const mods = MODULE_ORDER.map((s) => data.find((p) => p.slug === s)).filter(Boolean);
  if (mods.length === 0) return null;
  return (
    <section data-testid="home-build-your-way" className="container-hw py-16 lg:py-24">
      <Reveal className="grid gap-8 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <p className="eyebrow">Build it your way</p>
          <h2 className="t-h2 mt-3">Closed. Lifted. Drawered. One bay at a time.</h2>
          <p className="t-lead mt-5 max-w-2xl">Every Pustak Rack bay is 96.4 x 23.5 x 22.2 cm, so any module fits any bay. Close a bay with doors, lift a shelf into a standing desk, or slide in drawers for the clutter.</p>
        </div>
        <Link to="/shop/modules" data-testid="modules-shop-link" className="btn-outline justify-self-start lg:col-span-5 lg:justify-self-end">All modules <ArrowRight className="h-4 w-4" /></Link>
      </Reveal>
      <Stagger className="mt-12 grid gap-6 md:grid-cols-3">
        {mods.map((m) => (
          <Item key={m.slug}>
            <article data-testid={`module-card-${m.slug}`} className="group flex flex-col">
              <Link to={`/product/${m.slug}`} className="render card-hover block aspect-[4/5]">
                <img src={m.images[0]} alt={`${m.name} fitted in a Pustak Rack`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                <span className="absolute bottom-3 left-3 rounded-full bg-linen/95 px-3 py-1 text-xs font-semibold backdrop-blur">{MODULE_VIEWS[m.slug]}</span>
              </Link>
              <div className="mt-5 flex items-start justify-between gap-4 px-1">
                <div>
                  <Link to={`/product/${m.slug}`} className="t-h3 hover:text-sheesham">{m.name}</Link>
                  <p className="t-small mt-2 leading-relaxed">{m.tagline}</p>
                </div>
                <p className="price shrink-0 text-xl">{inr(m.price)}</p>
              </div>
              <ul className="mt-4 space-y-1.5 px-1 text-sm text-muted-foreground">
                {m.features.slice(0, 2).map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-neem" />{f}</li>)}
              </ul>
              <button data-testid={`module-add-${m.slug}`} onClick={() => addToCart(m)} className="btn-sheesham mt-5 self-start py-2.5 text-sm"><Plus className="h-4 w-4" /> Add to cart</button>
            </article>
          </Item>
        ))}
      </Stagger>
    </section>
  );
};

const AREAS = ["Gachibowli", "Kondapur", "Madhapur", "HITEC City", "Financial District", "Kokapet", "Manikonda", "Kukatpally", "Miyapur", "Banjara Hills", "Jubilee Hills", "Begumpet", "Secunderabad", "Tolichowki", "LB Nagar", "Uppal", "Kompally", "Shamshabad"];

const Localities = () => (
  <section data-testid="home-localities" className="container-hw py-8 lg:py-16">
    <div className="card grid gap-10 p-6 sm:p-10 lg:grid-cols-12 lg:p-14">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow">Hyderabad only, for now</p>
        <h2 className="t-h2 mt-3">Made for Hyderabad. Delivered free within 24 hours.</h2>
        <p className="t-lead mt-5">We are a Hyderabad business and deliver only in Hyderabad and Secunderabad for now. Our own team carries the box to your door, lift or no lift. Free, always.</p>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-7">
        <div className="flex flex-wrap gap-2">
          {AREAS.map((a) => <span key={a} className="chip px-3.5 py-1.5 text-sm">{a}</span>)}
        </div>
        <div className="mt-8 max-w-md rounded-2xl bg-linen p-5"><PincodeChecker compact /></div>
      </Reveal>
    </div>
  </section>
);

const Testimonials = () => {
  const { data = [] } = useQuery({ queryKey: ["recent-reviews"], queryFn: () => http.get("/reviews/recent").then((r) => r.data) });
  if (data.length === 0) return null;
  return (
    <section data-testid="home-testimonials" className="py-16 lg:py-24">
      <div className="container-hw">
        <Reveal><p className="eyebrow">Built in Hyderabad homes</p><h2 className="t-h2 mt-3">Notes from the floor.</h2></Reveal>
        <div className="no-scrollbar mt-10 flex snap-x gap-5 overflow-x-auto pb-2">
          {data.map((r) => (
            <figure key={r.id} className="card w-[300px] shrink-0 snap-start p-6 sm:w-[360px]">
              <Stars value={r.rating} />
              <blockquote className="t-h4 mt-4 font-sans font-medium leading-snug">"{r.comment}"</blockquote>
              <figcaption className="t-small mt-4">{r.name}, {r.city}, Hyderabad</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  ["Do I need a carpenter or special tools?", "No. The Pustak Rack comes with the hex key, numbered parts and an illustrated guide. If you can follow a recipe, you can build it."],
  ["How long does it take to build?", "About 40 minutes for two people. One of you holds, the other turns the key."],
  ["How big is the Pustak Rack?", "It stands 212.4 cm tall, 100 cm wide and just 23.5 cm deep, so it sits flat against a wall without eating into the room. The 7 shelves are 96.4 cm wide and 24 cm apart, with the lowest one 30 cm off the floor."],
  ["Can I add shelves later?", "Yes. That's the point of modular. You can add a shelf anytime, and add-ons fit the same system."],
  ["Can I close some shelves, or add a drawer or a desk?", "Yes. Every bay is 96.4 x 23.5 x 22.2 cm, so the Door module (₹999), Drawer module (₹1,499) and Lift-top module (₹1,999) slide into any bay. Close a bay for a clean view, add two drawers for clutter, or lift a shelf 12 cm up and 25 cm forward into a standing work or dine surface. Add them with your rack or later."],
  ["Is it safe with kids around?", "Every rack includes a wall anchor kit. Fix it to the wall, especially if you have small children or pets."],
  ["How much does delivery cost?", "Nothing. Anywhere in Hyderabad and Secunderabad, from Kompally to Shamshabad, the box arrives within 24 hours, free. We deliver only within Hyderabad and Secunderabad for now."],
  ["Is GST included in the price?", "The Pustak Rack is priced at ₹4,999 excluding tax. GST is added at checkout so you see the full amount before you place the order."],
  ["How do I pay?", "Right now you pay by cash or UPI when the box arrives at your door. Online payment is coming soon."],
  ["What if a part is damaged or missing?", "Email care@hwfurniture.in with a photo. In Hyderabad we send the replacement part free within 2 working days."],
];

const SeoContent = () => (
  <section data-testid="home-seo-content" aria-labelledby="seo-heading" className="container-hw grid gap-10 border-t border-jute/50 py-16 lg:grid-cols-12 lg:py-24">
    <Reveal className="lg:col-span-4">
      <p className="eyebrow">Modular furniture, explained</p>
      <h2 id="seo-heading" className="t-h2 mt-3">The DIY way to furnish a Hyderabad home.</h2>
    </Reveal>
    <Stagger className="grid gap-8 sm:grid-cols-2 lg:col-span-8">
      {[
        ["What is DIY modular furniture?", <>Modular furniture is made of standard parts that fit together and can be added to later. DIY, or ready-to-assemble, means it arrives flat-packed and you build it yourself at home. H&amp;W designs every piece so two people can put it together in one evening with a single hex key.</>],
        ["Why flat-pack furniture suits Hyderabad flats", <>From high-rises in Gachibowli and Kokapet to older buildings in Secunderabad, a flat box fits most lifts and staircases. Bolts instead of glue mean your modular bookshelf comes apart cleanly when you move flats.</>],
        ["A modular bookshelf that grows", <>The <Link to="/product/pustak-rack" className="font-semibold text-foreground underline underline-offset-4">Pustak Rack</Link> is a 7-shelf open bookshelf and storage rack in Oak or Walnut, 100 cm wide, 23.5 cm deep and 212.4 cm tall. Start with one, add a shelf anytime, and slide <Link to="/shop/modules" className="font-semibold text-foreground underline underline-offset-4">drawer, lift-top or door modules</Link> into any bay.</>],
        ["Buying modular furniture online in Hyderabad", <>Shop online at an honest price of ₹4,999 + GST, pay by cash or UPI on delivery, and get 24 Hrs free delivery anywhere in Hyderabad and Secunderabad. No carpenter, no waiting for a fitting slot.</>],
      ].map(([h, body]) => (
        <Item key={h}><h3 className="t-h4">{h}</h3><p className="t-body mt-3 text-muted-foreground">{body}</p></Item>
      ))}
    </Stagger>
  </section>
);

const FAQ = () => (
  <section id="faq" data-testid="home-faq" className="container-hw grid scroll-mt-28 gap-10 py-16 lg:grid-cols-12 lg:py-24">
    <Reveal className="lg:col-span-4">
      <p className="eyebrow">Questions</p>
      <h2 className="t-h2 mt-3">Before you order.</h2>
      <p className="t-lead mt-5">Still unsure? Write to <a href="mailto:care@hwfurniture.in" className="font-semibold text-foreground underline underline-offset-4">care@hwfurniture.in</a>.</p>
    </Reveal>
    <Reveal delay={0.1} className="lg:col-span-8">
      <Accordion type="single" collapsible className="card divide-y divide-jute/50 px-6 sm:px-8">
        {FAQS.map(([q, a], i) => (
          <AccordionItem key={q} value={`f${i}`} className="border-0">
            <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left text-base font-semibold hover:no-underline">{q}</AccordionTrigger>
            <AccordionContent className="t-body pb-5 text-muted-foreground">{a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="DIY Modular Furniture in Hyderabad | Flat-Pack Bookshelf | H&W Furniture"
        description="Shop DIY modular furniture in Hyderabad. The Pustak Rack is a 7-shelf flat-pack modular bookshelf in Oak or Walnut you build yourself, no carpenter needed. ₹4,999 + GST, 24 Hrs free delivery."
        path="/"
        jsonLd={[faqLd(FAQS)]}
      />
      <Hero />
      <Ticker />
      <Lineup />
      <div className="container-hw"><InHomes /></div>
      <WhyDIY />
      <HowItBuilds />
      <Modular />
      <BuildYourWay />
      <Localities />
      <Testimonials />
      <SeoContent />
      <FAQ />
    </>
  );
}
